import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { env } from '@/config/env';
import {
  DEFAULT_VISUAL_EFFECTS_CONFIG,
  createDefaultPageConfig,
  PAGE_TYPES,
  type VisualEffectsConfig,
  type VisualEffectsContextValue,
  type PageType,
  type PageEffectsConfig,
  type StateType,
  type Season,
  type DeviceCapability,
  type ParticleShape,
} from '@/types/visualEffects';
import {
  detectDeviceCapability,
  getEffectivePerformance,
  getOptimalConfig,
} from '@/lib/visualEffects/performanceDetector';
import { getCurrentSeason, isShabbat as checkIsShabbat } from '@/utils/jewishSeasons';

// ============================================
// MIGRATION HELPER
// ============================================

/**
 * Migrate and deep-merge page config from Firestore with defaults.
 * Handles old 'shape' (singular) to new 'shapes' (array) migration.
 */
function migratePageConfig(
  pageId: PageType,
  firestoreConfig: Partial<PageEffectsConfig> | undefined
): PageEffectsConfig {
  const defaultConfig = createDefaultPageConfig(pageId);

  if (!firestoreConfig) {
    return defaultConfig;
  }

  // Deep merge particles config
  const particles = { ...defaultConfig.particles };
  if (firestoreConfig.particles) {
    Object.assign(particles, firestoreConfig.particles);

    // Migration: convert old 'shape' to new 'shapes' array
    const oldShape = (firestoreConfig.particles as unknown as { shape?: ParticleShape }).shape;
    if (oldShape && !firestoreConfig.particles.shapes) {
      particles.shapes = [oldShape];
    }

    // Ensure shapes is always an array
    if (!particles.shapes || !Array.isArray(particles.shapes) || particles.shapes.length === 0) {
      particles.shapes = defaultConfig.particles.shapes;
    }

    // Ensure new properties have defaults
    if (particles.lensBokeEnabled === undefined) particles.lensBokeEnabled = false;
    if (particles.lensBokeCount === undefined) particles.lensBokeCount = 10;
    if (particles.lensBokeSize === undefined) particles.lensBokeSize = 40;
  }

  return {
    ...defaultConfig,
    ...firestoreConfig,
    pageId, // Always ensure pageId is correct
    gradient: { ...defaultConfig.gradient, ...firestoreConfig.gradient },
    particles,
    blur: { ...defaultConfig.blur, ...firestoreConfig.blur },
    seasonal: { ...defaultConfig.seasonal, ...firestoreConfig.seasonal },
  };
}

// ============================================
// CONTEXT
// ============================================

const VisualEffectsContext = createContext<VisualEffectsContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface VisualEffectsProviderProps {
  children: ReactNode;
}

export function VisualEffectsProvider({ children }: VisualEffectsProviderProps) {
  // Configuration state
  const [config, setConfig] = useState<VisualEffectsConfig>({
    ...DEFAULT_VISUAL_EFFECTS_CONFIG,
    updatedAt: undefined,
    updatedBy: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Device capability
  const [deviceCapability, setDeviceCapability] = useState<DeviceCapability>('high');

  // Shabbat status
  const [isShabbat, setIsShabbat] = useState(false);

  // Temporary state overlay
  const [temporaryState, setTemporaryState] = useState<StateType | null>(null);
  const stateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect device capability on mount
  useEffect(() => {
    const capability = detectDeviceCapability();
    setDeviceCapability(capability);

    // Listen for reduced motion preference changes
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setDeviceCapability(detectDeviceCapability());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Subscribe to Firestore config
  useEffect(() => {
    const configRef = doc(db, 'artifacts', env.appId, 'config', 'visualEffects');

    const unsubscribe = onSnapshot(
      configRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as Partial<VisualEffectsConfig>;

          // Deep merge each page config with its defaults
          const migratedPages: Record<PageType, PageEffectsConfig> = {} as Record<
            PageType,
            PageEffectsConfig
          >;
          for (const pageId of PAGE_TYPES) {
            const firestorePageConfig = data.pages?.[pageId];
            migratedPages[pageId] = migratePageConfig(pageId, firestorePageConfig);
          }

          setConfig({
            ...DEFAULT_VISUAL_EFFECTS_CONFIG,
            ...data,
            pages: migratedPages,
          });
        } else {
          // Use defaults if no config exists
          setConfig({
            ...DEFAULT_VISUAL_EFFECTS_CONFIG,
            updatedAt: undefined,
            updatedBy: undefined,
          });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading visual effects config:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Check Shabbat status periodically
  useEffect(() => {
    const updateShabbatStatus = () => setIsShabbat(checkIsShabbat());
    updateShabbatStatus();

    // Check every 5 minutes
    const interval = setInterval(updateShabbatStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup state timeout on unmount
  useEffect(() => {
    return () => {
      if (stateTimeoutRef.current) {
        clearTimeout(stateTimeoutRef.current);
      }
    };
  }, []);

  // Compute current season
  const currentSeason = useMemo<Exclude<Season, 'auto'>>(() => {
    // Global override takes precedence
    if (config.globalSeasonal.season !== 'auto') {
      return config.globalSeasonal.season;
    }

    // Shabbat overrides during Shabbat
    if (isShabbat) {
      return 'shabbat';
    }

    // Use simple season detection
    const detected = getCurrentSeason();
    return detected || 'spring';
  }, [config.globalSeasonal.season, isShabbat]);

  // Compute effective performance level
  const effectivePerformance = useMemo<DeviceCapability>(() => {
    return getEffectivePerformance(config.performanceMode, deviceCapability);
  }, [config.performanceMode, deviceCapability]);

  // Trigger temporary state
  const triggerState = useCallback((state: StateType, durationMs = 3000) => {
    // Clear existing timeout
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
    }

    setTemporaryState(state);

    // Auto-clear after duration
    stateTimeoutRef.current = setTimeout(() => {
      setTemporaryState(null);
      stateTimeoutRef.current = null;
    }, durationMs);
  }, []);

  // Clear temporary state
  const clearState = useCallback(() => {
    if (stateTimeoutRef.current) {
      clearTimeout(stateTimeoutRef.current);
      stateTimeoutRef.current = null;
    }
    setTemporaryState(null);
  }, []);

  // Get config for a specific page
  const getPageConfig = useCallback(
    (page: PageType): PageEffectsConfig => {
      const pageConfig = config.pages[page] || createDefaultPageConfig(page);

      // Apply performance optimizations
      return getOptimalConfig(pageConfig, effectivePerformance);
    },
    [config.pages, effectivePerformance]
  );

  // Check if effects are enabled for a page
  const isPageEffectsEnabled = useCallback(
    (page: PageType): boolean => {
      if (!config.enabled) return false;

      // Use fallback config if page config doesn't exist
      const pageConfig = config.pages[page] || createDefaultPageConfig(page);

      // At least one effect must be enabled
      return pageConfig.gradient.enabled || pageConfig.particles.enabled;
    },
    [config.enabled, config.pages]
  );

  const value = useMemo<VisualEffectsContextValue>(
    () => ({
      config,
      isLoading,
      deviceCapability,
      effectivePerformance,
      currentSeason,
      isShabbat,
      temporaryState,
      triggerState,
      clearState,
      getPageConfig,
      isPageEffectsEnabled,
    }),
    [
      config,
      isLoading,
      deviceCapability,
      effectivePerformance,
      currentSeason,
      isShabbat,
      temporaryState,
      triggerState,
      clearState,
      getPageConfig,
      isPageEffectsEnabled,
    ]
  );

  return <VisualEffectsContext.Provider value={value}>{children}</VisualEffectsContext.Provider>;
}

// ============================================
// HOOK
// ============================================

// eslint-disable-next-line react-refresh/only-export-components
export function useVisualEffectsContext(): VisualEffectsContextValue {
  const context = useContext(VisualEffectsContext);
  if (!context) {
    throw new Error('useVisualEffectsContext must be used within VisualEffectsProvider');
  }
  return context;
}
